'use client';
import React, { useState } from 'react';
import { Search, SlidersHorizontal, ChevronDown } from 'lucide-react';

const genres = [
  { id: 'genre-all', label: 'Të Gjitha' },
  { id: 'genre-action', label: 'Aksion' },
  { id: 'genre-drama', label: 'Dramë' },
  { id: 'genre-comedy', label: 'Komedi' },
  { id: 'genre-scifi', label: 'Sci-Fi' },
  { id: 'genre-thriller', label: 'Thriller' },
  { id: 'genre-horror', label: 'Horror' },
  { id: 'genre-romance', label: 'Romancë' },
  { id: 'genre-animation', label: 'Animacion' },
  { id: 'genre-documentary', label: 'Dokumentar' },
  { id: 'genre-adventure', label: 'Aventurë' },
];

const years = ['Të Gjitha', '2024', '2023', '2022', '2021', '2020', '2019'];
const ratings = ['Të Gjitha', '9+', '8+', '7+', '6+'];
const sortOptions = ['Më të Rejat', 'Vlerësimi Më i Lartë', 'Emri A-Z', 'Vitin'];

interface GenreFilterRowProps {
  onFilterChange?: (filters: { genre: string; year: string; rating: string; search: string }) => void;
}

export default function GenreFilterRow({ onFilterChange }: GenreFilterRowProps) {
  const [activeGenre, setActiveGenre] = useState('genre-all');
  const [searchVal, setSearchVal] = useState('');
  const [year, setYear] = useState('Të Gjitha');
  const [rating, setRating] = useState('Të Gjitha');
  const [sort, setSort] = useState('Më të Rejat');
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div className="py-6 space-y-4">
      {/* Search + Filter Toggle Row */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Kërko filma, regjisorë..."
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-600 border transition-all duration-150 ${
            showFilters
              ? 'bg-primary/15 text-primary border-primary/30' :'bg-muted text-secondary-foreground border-border hover:bg-border'
          }`}
        >
          <SlidersHorizontal size={15} />
          Filtrat
          <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="px-4 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
          aria-label="Rendit sipas"
        >
          {sortOptions.map((o) => (
            <option key={`sort-${o}`} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border fade-in flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Viti:</span>
            <div className="flex gap-1 flex-wrap">
              {years.map((y) => (
                <button
                  key={`year-${y}`}
                  onClick={() => setYear(y)}
                  className={`px-3 py-1 rounded-lg text-xs font-600 transition-all duration-150 ${
                    year === y
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-secondary-foreground hover:bg-border'
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-600 text-muted-foreground uppercase tracking-wider">IMDb:</span>
            <div className="flex gap-1">
              {ratings.map((r) => (
                <button
                  key={`rating-${r}`}
                  onClick={() => setRating(r)}
                  className={`px-3 py-1 rounded-lg text-xs font-600 transition-all duration-150 ${
                    rating === r
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-muted text-secondary-foreground hover:bg-border'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Genre Chips */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
        {genres.map((g) => (
          <button
            key={g.id}
            onClick={() => setActiveGenre(g.id)}
            className={`shrink-0 px-4 py-2 rounded-xl text-sm font-600 transition-all duration-150 ${
              activeGenre === g.id
                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                : 'bg-muted text-secondary-foreground border border-border hover:bg-border hover:text-foreground'
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>
    </div>
  );
}